import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { fehlendeFunktion, upgradeAnbieten } from '../billtoro/upgrade';
import type { Tarif } from '../billtoro/types';
import { fmtDate } from '../lib/format';
import { useToast } from '../lib/toast';
import { dez, kehrwert, kursFeld, kursNorm, waehrungName, WAEHRUNG_CODES, WAEHRUNGEN, weichtAb, type Waehrung } from './waehrung';
import './formular.css';

// Währung und Kurs eines Belegs (Rechnung, Angebot, Auftragsbestätigung, Lieferschein). Kurs: Euro je 1 Einheit, wie der
// Server ihn speichert; das Feld lässt ihn auch andersherum eingeben (1 € = … USD). Vorschlag: der EZB-Referenzkurs zum
// Tag des Kurses (GET …/kurs, entprellt); unverändert schickt das Formular den vollen Kurs der Route (sonst wäre die
// Quelle „eigen“). Ohne die Funktion im Tarif (Billtoro Kostenlos) nur Euro, dazu „Andere Währung …“ (Fenster „Mehr mit
// Billtoro“); dann fragt die Seite nie nach einem Kurs (der Server antwortet mit 402).

// Antwort der Route: Vorschlag oder warum es keinen gibt
type KursAntwort =
  | { waehrung: Waehrung; kurs: string; ezb: string; datum: string; quelle: 'ezb' }
  | { waehrung: Waehrung; kurs: null; grund: 'keine_ezb' | 'offline' | 'zu_alt' };

// Kurs im Feld: Euro je Einheit (6 Stellen) oder andersherum Einheiten je Euro
const anzeige = (kurs: string, andersrum: boolean) => (andersrum ? dez(kehrwert(kurs), 6, 0) : kursFeld(kurs));

// o.firmId: Firma (für die Route); o.tarif: Billtoro-Tarif (Kanzlei: null, alles da); o.datum: Belegdatum (Tag des Kurses,
// solange „Kurs vom“ nicht geändert ist); o.mitDatum: „Kurs vom“ zeigen und mitschicken (nur die Rechnung);
// o.preiseDa: beim Wechsel der Währung der Hinweis, dass die Preise bleiben
export function useWaehrung(o: { firmId: string; tarif: Tarif | null | undefined; datum: string; mitDatum?: boolean; preiseDa?: boolean }) {
  const toast = useToast();
  const [w, setWState] = useState<Waehrung>('EUR');
  const [text, setText] = useState('');
  const [andersrum, setAndersrum] = useState(false);
  // Tag des Kurses (leer: das Belegdatum)
  const [kursTag, setKursTag] = useState('');
  const [antwort, setAntwort] = useState<KursAntwort | null>(null);
  // zuletzt von selbst eingesetzter Kurs (voll) und wie er im Feld steht: steht er unverändert da, geht der volle Kurs mit
  const gesetzt = useRef<{ kurs: string; text: string; andersrum: boolean } | null>(null);
  // der Nutzer hat selbst einen Kurs eingetragen: ein neuer Vorschlag ersetzt ihn nicht (Refs: gelesen, wenn die
  // Antwort kommt)
  const eigen = useRef(false);
  const ar = useRef(false);
  ar.current = andersrum;
  const darf = !fehlendeFunktion(o.tarif, 'waehrung');
  const tag = kursTag || o.datum;
  const fremd = w !== 'EUR';

  function einsetzen(kurs: string, umgekehrt = ar.current) {
    const t = anzeige(kurs, umgekehrt);
    gesetzt.current = { kurs, text: t, andersrum: umgekehrt };
    setText(t);
  }

  // Vorschlag holen, wenn sich Währung oder Tag ändern (entprellt); offline oder Fehler: kein Vorschlag
  useEffect(() => {
    setAntwort(null);
    if (!fremd || !darf || !/^\d{4}-\d{2}-\d{2}$/.test(tag)) return;
    let live = true;
    const t = setTimeout(() => {
      api
        .get<KursAntwort>(`/api/rechnungstool/${o.firmId}/kurs?waehrung=${w}&datum=${tag}`)
        .then((a) => {
          if (!live) return;
          setAntwort(a);
          if (a.kurs !== null && !eigen.current) einsetzen(a.kurs);
          // kein Vorschlag mehr: ein von selbst eingesetzter alter Kurs fällt weg
          else if (a.kurs === null && !eigen.current && gesetzt.current) {
            gesetzt.current = null;
            setText('');
          }
        })
        .catch(() => live && setAntwort({ waehrung: w, kurs: null, grund: 'offline' }));
    }, 300);
    return () => {
      live = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w, tag, darf]);

  // Kurs für den Body: der volle Vorschlag, solange er unverändert im Feld steht; sonst der eingetragene (andersherum:
  // umgerechnet); unlesbar so, wie getippt (der Server meldet es); leer: null
  const g = gesetzt.current;
  const norm = kursNorm(text);
  const kurs: string | null = !fremd
    ? null
    : g && g.text === text && g.andersrum === andersrum
      ? g.kurs
      : norm
        ? andersrum
          ? kehrwert(norm)
          : norm
        : text.trim() || null;
  const lesbar = !!kurs && !!kursNorm(kurs);

  return {
    w,
    fremd,
    darf,
    text,
    andersrum,
    tag,
    kursTag,
    antwort,
    mitDatum: !!o.mitDatum,
    // Kurs (Euro je Einheit) zum Rechnen in der Anzeige; null: keiner oder unlesbar
    kurs: lesbar ? kurs : null,
    tarif: o.tarif,
    // Währung wählen (Nutzer, Kunde, Vorlage): Kurs neu; mit hinweis der Toast, wenn schon Preise da sind
    setW(neu: Waehrung, hinweis = true) {
      if (neu === w) return;
      setWState(neu);
      setText('');
      setAndersrum(false);
      eigen.current = false;
      gesetzt.current = null;
      if (hinweis && o.preiseDa) toast('Die Preise bleiben, wie sie sind. Bitte prüfen.');
    },
    // gespeicherter Stand (Entwurf): Währung, Kurs (Euro je Einheit) und Tag des Kurses
    laden(neu: Waehrung, k?: string | null, tagK?: string | null) {
      setWState(neu);
      setAndersrum(false);
      setKursTag(tagK && /^\d{4}-\d{2}-\d{2}$/.test(tagK) ? tagK : '');
      const n = neu !== 'EUR' ? kursNorm(k ?? null) : null;
      eigen.current = !!n;
      if (n) einsetzen(n, false);
      else {
        gesetzt.current = null;
        setText('');
      }
    },
    setText(v: string) {
      setText(v);
      eigen.current = v.trim() !== '';
    },
    setKursTag(v: string) {
      setKursTag(v === o.datum ? '' : v);
    },
    // Feld umschalten: der Kurs bleibt derselbe, nur andersherum geschrieben
    umschalten() {
      const um = !andersrum;
      setAndersrum(um);
      ar.current = um;
      if (lesbar) einsetzen(kurs!, um);
    },
    // Body: Währung, Kurs und (Rechnung) Tag des Kurses; Euro ohne Kurs
    body: {
      waehrung: w,
      kurs,
      ...(o.mitDatum ? { kursDatum: fremd && kursTag ? kursTag : null } : {}),
    },
  };
}
export type WaehrungStand = ReturnType<typeof useWaehrung>;

// Auswahl der Währung: „Euro (EUR)“ und die anderen mit Namen. Ohne die Funktion im Tarif: Euro, die gerade gewählte und
// „Andere Währung …“ (Fenster „Mehr mit Billtoro“). ohneAed: ohne Währungen, für die die EZB keinen Kurs herausgibt
// (wiederkehrende Rechnungen nehmen immer den EZB-Kurs).
export function WaehrungWahl({
  value,
  onChange,
  tarif,
  ohneAed = false,
  className = 'field',
  label = 'Währung',
  fest = false,
}: {
  value: Waehrung;
  onChange(w: Waehrung): void;
  tarif: Tarif | null | undefined;
  ohneAed?: boolean;
  className?: string;
  label?: string;
  fest?: boolean;
}) {
  const darf = !fehlendeFunktion(tarif, 'waehrung');
  const codes = WAEHRUNG_CODES.filter(
    (c) => (c === 'EUR' || !ohneAed || WAEHRUNGEN[c].ezb || c === value) && (darf || c === 'EUR' || c === value),
  );
  return (
    <label className={className}>
      <span>{label}</span>
      <select
        value={value}
        disabled={fest}
        onChange={(e) => {
          if (e.target.value === 'mehr') upgradeAnbieten(tarif, 'waehrung');
          else onChange(e.target.value as Waehrung);
        }}
      >
        {codes.map((c) => (
          <option key={c} value={c}>
            {waehrungName(c)}
          </option>
        ))}
        {!darf && <option value="mehr">Andere Währung …</option>}
      </select>
    </label>
  );
}

// Block unter den Daten des Belegs: Währung, bei Fremdwährung Kurs, „Kurs vom“ (Rechnung), Vorschlag und Hinweise.
// bic: BIC in „Meine Angaben“ (fehlt sie, ein Hinweis bei Fremdwährung; nicht angegeben: ohne Hinweis); fest: die
// Währung kommt von der Quelle (Auftragsbestätigung aus dem Angebot, Lieferschein aus der AB), nur der Kurs ist änderbar
export function WaehrungFeld({ h, bic, fest = false }: { h: WaehrungStand; bic?: string | null; fest?: boolean }) {
  const a = h.antwort;
  const vorschlag = a && a.kurs !== null ? a : null;
  const abweichung = !!vorschlag && !!h.kurs && weichtAb(h.kurs, vorschlag.kurs);
  return (
    <div className="rt-grid wf">
      <WaehrungWahl value={h.w} tarif={h.tarif} className="field wf-w" fest={fest} onChange={(v) => h.setW(v)} />
      {h.fremd && (
        <>
          <label className="field wf-kurs">
            <span>Kurs</span>
            <span className="wf-zeile">
              <span className="wf-1">{h.andersrum ? '1 € =' : `1 ${h.w} =`}</span>
              <input
                aria-label="Kurs"
                value={h.text}
                inputMode="decimal"
                autoComplete="off"
                placeholder={h.andersrum ? '1,0876' : '0,919456'}
                onChange={(e) => h.setText(e.target.value)}
              />
              <span className="wf-e">{h.andersrum ? h.w : '€'}</span>
            </span>
          </label>
          {h.mitDatum && (
            <label className="field wf-tag">
              <span>Kurs vom</span>
              <input type="date" value={h.tag} onChange={(e) => h.setKursTag(e.target.value)} />
            </label>
          )}
          <div className="wf-um">
            <button type="button" className="linkbtn" onClick={h.umschalten}>
              {h.andersrum ? `andersherum eingeben (1 ${h.w} = … €)` : `andersherum eingeben (1 € = … ${h.w})`}
            </button>
          </div>
          <div className="rt-span4 wf-hinweise">
            {vorschlag ? (
              <small className="muted">
                Vorschlag: EZB-Referenzkurs vom {fmtDate(vorschlag.datum)} (1 € = {dez(vorschlag.ezb, 6, 4)} {h.w}).
              </small>
            ) : a && a.kurs === null ? (
              <small className="muted">
                {a.grund === 'keine_ezb'
                  ? `Für ${h.w} gibt die EZB keinen Kurs heraus. Bitte den Kurs eintragen.`
                  : 'Kein EZB-Kurs abrufbar. Bitte den Kurs eintragen.'}
              </small>
            ) : null}
            <small className="muted">Steuerlich gilt der Monatskurs des Bundesfinanzministeriums. Den Tageskurs nur mit Zustimmung des Finanzamts.</small>
            {abweichung && <p className="notice warn wf-warn">Der Kurs weicht stark vom EZB-Kurs ab. Bitte prüfen.</p>}
            {bic !== undefined && !bic?.trim() && (
              <p className="notice warn wf-warn">Für Zahlungen aus dem Ausland bitte Ihre BIC in Meine Angaben eintragen.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
